import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Badge,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { UserNotification, NotificationCategoryConfig, NotificationPriorityConfig } from '@/types/notifications';
import notificationService from '@/services/notificationService';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';

interface NotificationCenterProps {
  open: boolean;
  onClose: () => void;
}

type TabType = 'all' | 'unread' | 'action_required';

const NotificationCenter: React.FC<NotificationCenterProps> = ({ open, onClose }) => {
  const [notifications, setNotifications] = useState<UserNotification[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedTab, setSelectedTab] = useState<TabType>('unread');
  const [selectedNotification, setSelectedNotification] = useState<UserNotification | null>(null);

  // Load notifications
  useEffect(() => {
    if (!open) return;
    loadNotifications();
  }, [open]);

  const loadNotifications = async () => {
    setLoading(true);
    setError(null);
    const { data, error: err } = await notificationService.getUserNotifications();
    if (err) {
      setError(err);
    } else {
      setNotifications(data);
    }
    setLoading(false);
  };

  // Get filtered notifications based on tab
  const filteredNotifications = (() => {
    switch (selectedTab) {
      case 'unread':
        return notifications.filter((n) => n.status === 'unread');
      case 'action_required':
        return notifications.filter(
          (n) => n.category === 'action_required' && n.status === 'unread',
        );
      case 'all':
      default:
        return notifications;
    }
  })();

  const handleMarkAsRead = async (notif: UserNotification) => {
    const { error } = await notificationService.markNotificationAsRead(notif.id);
    if (!error) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notif.id ? { ...n, status: 'read', readAt: new Date().toISOString() } : n,
        ),
      );
      setSelectedNotification(null);
    }
  };

  const handleDelete = async (notif: UserNotification) => {
    const { error } = await notificationService.deleteNotificationForUser(notif.id);
    if (!error) {
      setNotifications((prev) =>
        prev.map((n) =>
          n.id === notif.id ? { ...n, status: 'deleted', deletedAt: new Date().toISOString() } : n,
        ),
      );
      setSelectedNotification(null);
    }
  };

  const handleMarkAllAsRead = async () => {
    const unreadIds = filteredNotifications
      .filter((n) => n.status === 'unread')
      .map((n) => n.id);
    if (unreadIds.length === 0) return;

    const { error } = await notificationService.markMultipleAsRead(unreadIds);
    if (!error) {
      loadNotifications();
    }
  };

  const getCategoryConfig = (category: string) => {
    return NotificationCategoryConfig[category as keyof typeof NotificationCategoryConfig] || NotificationCategoryConfig.informasi;
  };

  const getPriorityConfig = (priority: string) => {
    return NotificationPriorityConfig[priority as keyof typeof NotificationPriorityConfig] || NotificationPriorityConfig.medium;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>Notifikasi</span>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        {/* Tabs */}
        <Tabs
          value={selectedTab}
          onChange={(_, newValue) => setSelectedTab(newValue)}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={
              <Badge
                badgeContent={notifications.filter((n) => n.status === 'unread').length}
                color="error"
              >
                <span>Belum Dibaca</span>
              </Badge>
            }
            value="unread"
          />
          <Tab
            label={
              <Badge
                badgeContent={notifications.filter(
                  (n) => n.category === 'action_required' && n.status === 'unread',
                ).length}
                color="error"
              >
                <span>Tindakan</span>
              </Badge>
            }
            value="action_required"
          />
          <Tab label="Semua" value="all" />
        </Tabs>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={40} />
          </Box>
        )}

        {/* Notifications List */}
        {!loading && filteredNotifications.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography color="textSecondary">
              {selectedTab === 'unread' && 'Tidak ada notifikasi baru'}
              {selectedTab === 'action_required' && 'Tidak ada tindakan yang diperlukan'}
              {selectedTab === 'all' && 'Tidak ada notifikasi'}
            </Typography>
          </Box>
        )}

        {!loading && filteredNotifications.length > 0 && (
          <List sx={{ p: 0 }}>
            {filteredNotifications.map((notif, idx) => {
              const config = getCategoryConfig(notif.category);
              const priorityConfig = getPriorityConfig(notif.priority || 'medium');
              const isUnread = notif.status === 'unread';

              return (
                <ListItem
                  key={notif.id}
                  divider={idx < filteredNotifications.length - 1}
                  disablePadding
                  sx={{
                    bgcolor: isUnread ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                  }}
                >
                  <ListItemButton
                    onClick={() => setSelectedNotification(notif)}
                    sx={{
                      flexDirection: 'column',
                      alignItems: 'flex-start',
                      p: 2,
                      '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.04)' },
                    }}
                  >
                    {/* Header: Category Badge + Title + Time */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        width: '100%',
                        mb: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flex: 1 }}>
                        <span style={{ fontSize: '1.2em' }}>{config.icon}</span>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            fontWeight: isUnread ? 600 : 400,
                            flex: 1,
                            wordBreak: 'break-word',
                          }}
                        >
                          {notif.title}
                        </Typography>
                      </Box>
                      {isUnread && (
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            bgcolor: config.color,
                            ml: 1,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </Box>

                    {/* Content Preview */}
                    <Typography
                      variant="caption"
                      color="textSecondary"
                      sx={{
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                        mb: 1,
                      }}
                    >
                      {notif.content}
                    </Typography>

                    {/* Footer: Chips + Time */}
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '100%',
                        mt: 1,
                      }}
                    >
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        <Chip
                          label={config.label}
                          size="small"
                          sx={{
                            bgcolor: config.bgColor,
                            color: config.color,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                        <Chip
                          label={priorityConfig.label}
                          size="small"
                          sx={{
                            bgcolor: `${priorityConfig.color}20`,
                            color: priorityConfig.color,
                            fontWeight: 500,
                            fontSize: '0.7rem',
                          }}
                        />
                      </Box>
                      <Typography variant="caption" color="textSecondary">
                        {format(new Date(notif.createdAt), 'HH:mm', { locale: idLocale })}
                      </Typography>
                    </Box>
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        )}
      </DialogContent>

      {/* Detail & Actions */}
      {selectedNotification && (
        <DialogActions sx={{ flexDirection: 'column', alignItems: 'stretch', gap: 2, p: 2 }}>
          <Box sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              {selectedNotification.title}
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, lineHeight: 1.6 }}>
              {selectedNotification.content}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {format(new Date(selectedNotification.createdAt), 'dd MMMM yyyy HH:mm', {
                locale: idLocale,
              })}
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {selectedNotification.status === 'unread' && (
              <Button
                variant="outlined"
                startIcon={<CheckCircleIcon />}
                onClick={() => handleMarkAsRead(selectedNotification)}
                fullWidth
              >
                Tandai Dibaca
              </Button>
            )}
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => handleDelete(selectedNotification)}
              fullWidth
            >
              Hapus
            </Button>
            <Button variant="contained" onClick={() => setSelectedNotification(null)} fullWidth>
              Tutup
            </Button>
          </Box>
        </DialogActions>
      )}

      {!selectedNotification && filteredNotifications.length > 0 && (
        <DialogActions>
          {selectedTab !== 'all' && (
            <Button color="primary" onClick={handleMarkAllAsRead} variant="text">
              Tandai Semua Dibaca
            </Button>
          )}
          <Button onClick={onClose} variant="contained">
            Tutup
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default NotificationCenter;
